import { createApiClient } from "~/api/apiClient";
import type { components } from "~/api/generated/api-server-types";
import type { ToolStep } from "./useAgentChat";
import { replayProgressEvents } from "./useConversationProgress";

type StatusResponse = components["schemas"]["ConversationProgress"];

interface ActivityEvent {
  sequence: number;
  event: string;
  data: Record<string, unknown>;
}

/**
 * A reopened conversation's tool steps, read from the api-server so a history
 * load never cold-starts an Indy Lambda environment beside `/warmup`.
 * Pages may split a message or repeat the latest turn to cover index lag.
 */
export async function fetchConversationActivity(
  conversationId: string,
): Promise<Map<string, ToolStep[]>> {
  const client = createApiClient();
  const messages = new Map<string, Map<number, ActivityEvent>>();
  let cursor: Record<string, string> | null = null;
  do {
    // Annotated: `cursor` feeds the request and is read back from `data`.
    const { data }: { data?: StatusResponse } = await client.POST(
      "/indy/conversation/status",
      {
        body: {
          conversation_id: conversationId,
          include_activity: true,
          activity_cursor: cursor,
        },
      },
    );
    if (!data) throw new Error("Conversation activity unavailable");
    for (const row of data.activity ?? []) {
      const events = messages.get(row.assistant_message_id) ?? new Map();
      for (const event of row.events) {
        events.set(event.sequence, {
          ...event,
          data: {
            ...event.data,
            duration_ms: row.duration_ms ?? event.data.duration_ms,
            model_turn_number: row.model_turn_number,
          },
        });
      }
      messages.set(row.assistant_message_id, events);
    }
    cursor = data.activity_next_cursor ?? null;
  } while (cursor);
  return new Map(
    [...messages].map(([messageId, events]) => [
      messageId,
      replayProgressEvents(
        [...events.values()].sort((a, b) => a.sequence - b.sequence),
      ).steps,
    ]),
  );
}
